import { PERM } from "../../auth/rbac";
import type { RequestOptions } from "../client";
import type { ServiceName } from "../services";
import * as config from "./config";
import * as entities from "./entities";
import { map3dRoute } from "./map3d";
import {
  abcXyzMatrix,
  abcXyzReport,
  capacityReport,
  dashboardSummary,
  warehouseFill,
  discrepancyReport,
  incompleteOrdersReport,
  isoAgo,
  kpiReport,
  palletPlanFixture,
  pickingOrdersFixture,
  qualityReports,
  seeded,
  stockReport,
  tasksReport,
} from "./fixtures";

/**
 * Namuna so'rov yo'naltirgichi.
 *
 * Mijoz qatlami (`request`) namuna rejimida `fetch` o'rniga shu
 * yerga murojaat qiladi. Shunday qilingani sababli modullar
 * O'ZGARMAYDI: har bir ekranda «agar namuna bo'lsa» sharti
 * paydo bo'lsa, ikkita interfeysni parallel saqlash kerak
 * bo'lardi va ular vaqt o'tib bir-biridan ajralib ketardi.
 */

/** Tarmoq bor tuyg'usi: yuklanish holatlari ham sinalsin. */
const LATENCY_MS = 260;

/** Modullar orasida BIR XIL nusxa — o'zgarish saqlanib qoladi. */
const state = {
  blocks: entities.locationBlocks(),
  licensePlates: entities.licensePlates(),
  locations: entities.locations(),
  counts: entities.inventoryCounts(),
  staff: entities.dispatchStaff(),
  dispatch: entities.dispatchTasks(),
  finished: entities.finishedTasks(),
  decisions: entities.controlDecisions(),
  skus: entities.skus(),
  messages: entities.integrationMessages(),
  mappings: entities.integrationMappings(),
  notifications: entities.notifications(),
  offsite: entities.offsite(),
  onecAuth: entities.onecAuth(),
  pickingOrders: pickingOrdersFixture(),
};

export async function mockRequest<T>(
  service: ServiceName,
  path: string,
  options: RequestOptions,
): Promise<T> {
  await new Promise((resolve) => setTimeout(resolve, LATENCY_MS));

  const handler = route(path, options);
  if (handler === undefined) {
    throw new Error(`namuna ma'lumot yo'q: ${service} ${path}`);
  }
  return handler as T;
}

/**
 * Yo'l bo'yicha javob tanlanadi, servis nomi bo'yicha emas.
 *
 * Yo'llar butun tizimda noyob va ishlab chiqarishda ular bitta
 * gateway ortiga yig'iladi — servis nomi shu yerda ortiqcha
 * ajratgich bo'lardi.
 */
function route(path: string, options: RequestOptions): unknown {
  const method = options.method ?? "GET";
  const limit = Number(options.query?.limit ?? 100);

  // --- 3D xarita (o'z fayli; tegishli bo'lmasa davom etadi) ---
  const scene3d = map3dRoute(path, options);
  if (scene3d !== undefined) return scene3d;

  // --- Kirish ---
  if (path === "/api/v1/auth/login") return session();

  // --- Hisobotlar ---
  // Matritsa umumiy `{report}` yo'lidan OLDIN: aks holda
  // "abc-xyz/matrix" nomli hisobot so'ralgandek ko'rinardi.
  if (path === "/api/v1/reports/abc-xyz/matrix") return abcXyzMatrix();
  if (path === "/api/v1/reports/kpi/employees") {
    // Namuna: dastlabki to'rt xodim ishlagan, qolganlari nol — ro'yxat
    // natijasiz xodimni ham ko'rsatishi kerak.
    const items = entities.users().slice(0, 4).map((user, index) => {
      const tasks = 40 - index * 9;
      return {
        employee_id: user.id, task_count: tasks, line_count: tasks * 3, actual_qty: String(tasks * 17),
        work_seconds: tasks * 540, wait_seconds: tasks * 60, shortage_count: index % 2, excess_count: index === 2 ? 1 : 0,
        penalty_count: 0, active_days: 6 - index, open_tasks: index === 0 ? 2 : 0,
        last_completed_at: new Date(Date.now() - index * 3_600_000).toISOString(),
        by_type: [
          { task_type: "PICK", task_count: tasks - 5, line_count: (tasks - 5) * 3, actual_qty: String((tasks - 5) * 17),
            work_seconds: (tasks - 5) * 540, wait_seconds: 0, shortage_count: 0, excess_count: 0, penalty_count: 0 },
          { task_type: "PUTAWAY", task_count: 5, line_count: 15, actual_qty: "85",
            work_seconds: 2700, wait_seconds: 0, shortage_count: 0, excess_count: 0, penalty_count: 0 },
        ],
      };
    });
    return { from: options.query?.from ?? "", to: options.query?.to ?? "", items };
  }
  if (path.startsWith("/api/v1/reports/")) {
    const name = path.slice("/api/v1/reports/".length);
    return reportResult(name, options);
  }
  if (path === "/api/v1/reports-status") return { items: projectionStatus() };

  // --- Boshqaruv paneli ---
  if (path === "/api/v1/dashboard") {
    return dashboardSummary(periodDays(options), state.dispatch, options);
  }
  if (path === "/api/v1/warehouse-fill") return warehouseFill();

  // Yacheyka turiga bog'langan chegaralar (PAR-02): topologiya
  // ekrani vazn va SKU chegarasini shu yerdan oladi.
  if (path === "/api/v1/cell-type-defaults") {
    return page(
      [
        { cell_type: "Y1", max_weight_kg: "55", max_sku_count: 1 },
        { cell_type: "Y2", max_weight_kg: "80", max_sku_count: 1 },
        { cell_type: "PL", max_weight_kg: "800", max_sku_count: 3 },
        { cell_type: "P1", max_weight_kg: "800", max_sku_count: 3 },
        { cell_type: "P2", max_weight_kg: "1600", max_sku_count: 3 },
      ],
      limit,
    );
  }

  // Holat kesimidagi sanoq: «tekshirishga tayyor» va «yuklanishga
  // tayyor» ko'rsatkichlari shu yerdan.
  if (path === "/api/v1/picking/orders/counts") {
    const counts = new Map<string, number>();
    for (const order of state.pickingOrders) {
      counts.set(order.status, (counts.get(order.status) ?? 0) + 1);
    }
    // Namunada nazoratni kutayotgan buyurtma bo'lishi kerak —
    // aks holda karta doim nol ko'rinardi.
    counts.set("PICKED", (counts.get("PICKED") ?? 0) + 2);
    return page([...counts.entries()].map(([status, count]) => ({ status, count })), limit);
  }
  if (path === "/api/v1/shipments/counts") {
    return page(
      [
        { status: "DRAFT", count: 1 },
        { status: "STAGED", count: 3 },
        { status: "LOADING", count: 1 },
        { status: "COMPLETED", count: 24 },
      ],
      limit,
    );
  }

  // Namuna XLSX: namuna rejimida ham tugma ishlashi kerak.
  if (path === "/api/v1/locations/imports/template.xlsx") {
    return { items: [], has_more: false };
  }

  // --- Ombor xaritasi ---
  // Yig'indi BO'LAKLARDAN quriladi: ikkalasi alohida yozilsa,
  // panel bilan chizma bir-biriga mos kelmasdi.
  if (path === "/api/v1/map/summary") {
    const counts = new Map<string, {
      cell_count: number; occupied_cells: number; inactive_cells: number;
      blocked_cells: number; pallet_capacity: number; occupied_slots: number;
    }>();
    for (const section of mapSections()) {
      // Vazifa REYESTR QOIDASI bo'yicha (ZON-02): A korpusning
      // 1-3 qavati terish (OX), 4-6 qavati saqlash (MX). Yer
      // saqlashi — MX, operatsion zonalar esa o'z nomi bilan.
      const fn =
        section.kind === "RACK"
          ? (section.level ?? 0) <= 3
            ? "OX"
            : "MX"
          : section.kind === "FLOOR"
            ? "MX"
            : section.zone_code === "QAB"
              ? "RECEIPT"
              : "SHIPPING";
      const row = counts.get(fn) ?? {
        cell_count: 0, occupied_cells: 0, inactive_cells: 0,
        blocked_cells: 0, pallet_capacity: 0, occupied_slots: 0,
      };
      row.cell_count += section.cell_count;
      row.occupied_cells += section.occupied_cells;
      row.inactive_cells += section.inactive_cells;
      row.blocked_cells += section.blocked_cells;
      row.pallet_capacity += section.pallet_capacity;
      row.occupied_slots += section.occupied_slots;
      counts.set(fn, row);
    }
    return page(
      [...counts.entries()].map(([fn, row]) => ({ function: fn, ...row })),
      limit,
    );
  }
  if (path === "/api/v1/map/sections") {
    return { items: mapSections(), has_more: false };
  }
  if (path === "/api/v1/map/cells") {
    // `aisle` — butun qator: 3D ko'rinish barcha qavatni birdan
    // oladi. Namunada ham shunday bo'lmasa 3D bo'sh chiqardi.
    const aisle = String(options.query?.aisle ?? "");
    if (aisle) {
      const all = [];
      for (let level = 1; level <= 6; level += 1) {
        all.push(...mapCells(`${aisle}-${String(level).padStart(2, "0")}`));
      }
      return { items: all, has_more: false };
    }
    return { items: mapCells(String(options.query?.section ?? "")), has_more: false };
  }
  if (/^\/api\/v1\/map\/cells\/[^/]+\/contents$/.test(path)) {
    return mapCellContents(path.split("/")[5]!);
  }
  if (path === "/api/v1/map/heat") {
    return mapHeat(
      String(options.query?.section ?? ""),
      String(options.query?.aisle ?? ""),
      Number(options.query?.days ?? 30),
    );
  }

  // --- Pallet rejasi (OPT-03…05) ---
  if (path === "/api/v1/picking/orders") {
    const recipient = String(options.query?.recipient_warehouse ?? "");
    const closed = options.query?.closed_in_1c === true || options.query?.closed_in_1c === "true";
    return page(
      state.pickingOrders.filter((order) =>
        (!recipient || order.recipient_warehouse === recipient) &&
        (!closed || Boolean((order as { closed_in_1c_at?: string }).closed_in_1c_at))),
      limit,
    );
  }
  if (path === "/api/v1/picking/orders/recipient-warehouses") {
    const values = new Set(state.pickingOrders.map((order) => order.recipient_warehouse ?? "").filter(Boolean));
    return { items: [...values].sort() };
  }
  if (/^\/api\/v1\/picking\/orders\/[^/]+\/pallet-plan$/.test(path)) {
    return palletPlanFixture(path.split("/")[5]!);
  }

  // --- Palletlar va tara (LP) ---
  if (path === "/api/v1/license-plates") {
    const code = String(options.query?.code ?? "").toUpperCase();
    const rows = state.licensePlates.filter(
      (lp) =>
        (!options.query?.status || lp.status === options.query.status) &&
        (!options.query?.lp_type || lp.lp_type === options.query.lp_type) &&
        (!code || lp.lp_code.toUpperCase().startsWith(code)),
    );
    return page(rows, limit);
  }
  if (/^\/api\/v1\/license-plates\/[^/]+\/history$/.test(path)) {
    return page(entities.lpMovements(path.split("/")[4]!), limit);
  }
  if (/^\/api\/v1\/license-plates\/[^/]+$/.test(path)) {
    return state.licensePlates.find((lp) => lp.id === path.split("/")[4]);
  }

  // --- Manzillar: `ids` bilan kodga aylantirish ---
  if (path === "/api/v1/locations") {
    const wanted = String(options.query?.ids ?? "").split(",").filter(Boolean);
    const rows = wanted.length
      ? state.locations.filter((row) => wanted.includes(row.id))
      : state.locations;
    return page(rows, limit);
  }

  // --- Bloklar ---
  if (path === "/api/v1/location-blocks") {
    const status = options.query?.status;
    const rows = status
      ? state.blocks.filter((block) => block.status === status)
      : state.blocks;
    return page(rows, limit);
  }
  if (method === "POST" && /^\/api\/v1\/location-blocks\/[^/]+\/close$/.test(path)) {
    const id = path.split("/")[4]!;
    const block = state.blocks.find((item) => item.id === id);
    if (block) {
      block.status = "CLOSED";
      block.closed_at = new Date().toISOString();
      block.close_reason = String(
        (options.body as { reason?: string } | undefined)?.reason ?? "",
      );
      block.version += 1;
    }
    return block;
  }

  // --- Menejer qarorlari ---
  // --- 1C buyurtmalari (faqat chiqim; prixod — erkin sessiya) ---
  // Qo'lda kiritilgan hujjat: namunada ham ro'yxatga TUSHADI —
  // aks holda forma «ishladi» deb ko'rinib, natijasi yo'qolardi.
  if (method === "POST" && path === "/api/v1/picking/orders") {
    const body = options.body as Record<string, unknown>;
    const order = {
      id: `mock-so-${state.pickingOrders.length + 1}`,
      order_number: String(body.order_number ?? ""),
      external_id: String(body.external_id ?? ""),
      customer_id: String(body.customer_id ?? ""),
      recipient_warehouse: String(body.recipient_warehouse ?? ""),
      warehouse_code: String(body.warehouse_code ?? "A"),
      status: "CREATED", priority: 0, version: 1,
      created_at: new Date().toISOString(),
      lp_code: "",
    };
    state.pickingOrders = [order, ...state.pickingOrders];
    return order;
  }
  // --- Erkin qabul / vozvrat sessiyalari (2026-10-31) — namunada bo'sh ---
  if (path === "/api/v1/receiving-sessions") {
    return page([], limit);
  }

  if (method === "POST" && /^\/api\/v1\/picking\/orders\/[^/]+\/allocate$/.test(path)) {
    const id = path.split("/")[5]!;
    const order = state.pickingOrders.find((item) => item.id === id);
    if (!order) return undefined;
    order.status = "ALLOCATED";
    order.lp_code = order.lp_code || `LP${String(90 + state.dispatch.length).padStart(8, "0")}`;
    order.version += 1;
    state.dispatch = [
      { id: `tsk-pick-${id}`, task_type: "PICK", status: "RESERVED", priority: 30,
        required_role: "picker", warehouse_code: order.warehouse_code,
        source_process: "PICKING", order_id: id, version: 1,
        created_at: new Date().toISOString(), lines: [] },
      ...state.dispatch,
    ];
    return order;
  }
  if (/^\/api\/v1\/picking\/orders\/[^/]+$/.test(path)) {
    const order = pickingOrdersFixture().find((item) => item.id === path.split("/")[5]);
    return order && { ...order,
      lines: [{ id: "l1", line_number: 1, sku_id: "sku-1", requested_qty: "5", allocated_qty: "0", picked_qty: "0", unallocated_qty: "0" }] };
  }
  if (/^\/api\/v1\/counterparties\/[^/]+$/.test(path)) {
    const id = path.split("/")[4]!;
    return { id, name: counterpartyNames()[id] ?? "Kontragent", roles: ["CUSTOMER"], is_active: true };
  }
  // Ro'yxat `ids` bilan ham chaqiriladi: buyurtma tanlashda mijoz nomi
  // `<option>` matni ichida kerak — u yerda komponent ishlatib
  // bo'lmaydi, shuning uchun nomlar BITTA so'rovda olinadi.
  if (path === "/api/v1/counterparties") {
    const rows = Object.entries(counterpartyNames()).map(([id, name]) => ({
      id, name, roles: ["CUSTOMER"], is_active: true }));
    const ids = String(options.query?.ids ?? "").split(",").filter(Boolean);
    if (ids.length > 0) return page(rows.filter((row) => ids.includes(row.id)), limit);
    const q = String(options.query?.q ?? "").toLowerCase();
    return page(q ? rows.filter((row) => row.name.toLowerCase().includes(q)) : rows, limit);
  }

  // --- 1C integratsiyasi ---
  if (path === "/api/v1/integration/health") {
    const count = (direction: string) =>
      state.messages
        .filter((item) => item.direction === direction)
        .reduce<Record<string, number>>((acc, item) => ({ ...acc, [item.status]: (acc[item.status] ?? 0) + 1 }), {});
    return {
      enabled: true, callback_configured: true, inbound: count("INBOUND"), outbound: count("OUTBOUND"),
      oldest_pending_at: isoAgo(6), oldest_pending_seconds: 360, mapping_version: 14,
    };
  }
  // «1C holati» (namuna): avtomatik bog'lanmagan qiymatlar va «Склад получатель».
  if (path === "/api/v1/integration/onec-status") {
    return {
      waiting_values: [{ kind: "UOM", external_key: "коробка=600шт", messages: 1 }],
      unit_classifier: { units: 38, loaded_at: isoAgo(90) },
      recipients: {
        enabled: true, unresolved_keys: 1, unresolved_orders: 14, catalogs: ["Catalog_СтруктурныеЕдиницы"],
        queue: { PENDING: 3, DONE: 41, NOT_FOUND: 2 },
        items: [
          { key: "7deaa0bf-54b3-11f0-8d7c-000c29414c64", name: "GM Самарканд", resolved: true,
            display: "GM Самарканд", order_count: 27, last_seen: isoAgo(15) },
          { key: "ee503884-50b4-11f0-8d7b-000c29414c64", name: "", resolved: false,
            display: "Qabul ombori aniqlanmagan", order_count: 14, last_seen: isoAgo(40) },
        ],
      },
    };
  }
  // 1C hisobi: namunada SAQLANGAN, lekin `.env` dan emas.
  if (path === "/api/v1/integration/onec-auth") {
    if (method === "PUT") {
      const body = (options.body ?? {}) as Record<string, string | null>;
      state.onecAuth = { ...state.onecAuth,
        username: String(body.username ?? state.onecAuth.username),
        password_set: state.onecAuth.password_set || Boolean(body.password),
        configured: true,
        version: state.onecAuth.version + 1,
        updated_at: new Date().toISOString() };
    }
    return state.onecAuth;
  }
  if (method === "POST" && path === "/api/v1/integration/onec-auth/test") {
    return { ok: true, status: 200, detail: "200 OK" };
  }

  if (path === "/api/v1/integration/messages") {
    const query = options.query ?? {};
    const rows = state.messages.filter(
      (item) =>
        item.direction === (query.direction ?? "INBOUND") &&
        (!query.status || item.status === query.status) &&
        (!query.entity_id || item.entity_id.includes(String(query.entity_id))),
    );
    return page(rows.map(({ payload: _p, onec_payload: _o, ...rest }) => rest), limit);
  }
  if (path === "/api/v1/integration/dead-letter") {
    const review = ["WAITING_MAPPING", "REJECTED", "FAILED", "MANUAL_REVIEW", "DEAD_LETTER"];
    return { items: state.messages.filter((item) => review.includes(item.status)) };
  }
  if (method === "POST" && /^\/api\/v1\/integration\/messages\/[^/]+\/retry$/.test(path)) {
    const item = state.messages.find((row) => row.message_id === path.split("/")[5]);
    if (item) {
      item.status = item.direction === "INBOUND" ? "ACCEPTED" : "PENDING";
      item.attempts = item.direction === "INBOUND" ? (item.attempts ?? 0) : 0;
      item.allowed_actions = [];
    }
    return item;
  }
  if (/^\/api\/v1\/integration\/messages\/[^/]+$/.test(path)) {
    return state.messages.find((row) => row.message_id === path.split("/")[5]);
  }
  if (path === "/api/v1/integration/mappings") {
    return { mapping_version: 14, items: state.mappings.filter((row) => row.kind === (options.query?.kind ?? row.kind)) };
  }
  if (method === "PUT" && /^\/api\/v1\/integration\/mappings\/[^/]+\/[^/]+$/.test(path)) {
    const [, , , , , mappingKind, key] = path.split("/");
    const body = (options.body ?? {}) as { wms_value?: string; is_active?: boolean; note?: string };
    const row = state.mappings.find((item) => item.kind === mappingKind && item.external_key === decodeURIComponent(key!));
    if (row) Object.assign(row, body, { version: row.version + 1, updated_at: new Date().toISOString() });
    return row;
  }
  if (method === "POST" && path === "/api/v1/integration/reconciliations") {
    return {
      run: {
        id: "mock-run", operation_id: "mock-op", kind: "DELIVERY_ACK", status: "COMPLETED",
        total_items: 2, matched_items: 1, mismatched_items: 1, started_at: isoAgo(1), finished_at: isoAgo(1),
      },
      items: [
        { key: "msg-1c-ack-yoq", verdict: "MISSING_IN_1C", wms_value: "DELIVERED" },
        { key: "msg-tasdiqlandi", verdict: "MATCH", wms_value: "DELIVERED", onec_value: "ACKNOWLEDGED" },
      ],
    };
  }
  if (/^\/api\/v1\/integration\/reconciliations\/[^/]+$/.test(path)) {
    return {
      run: {
        id: path.split("/")[5], operation_id: "mock-op", kind: "DELIVERY_ACK", status: "COMPLETED",
        total_items: 0, matched_items: 0, mismatched_items: 0, started_at: isoAgo(1), finished_at: isoAgo(1),
      },
      items: [],
    };
  }

  // --- Import (fayl namuna rejimida o'qilmaydi) ---
  if (method === "POST" && /^\/api\/v1\/(master-data|locations|opening-balances)\/imports\/file$/.test(path)) {
    const body = (options.body ?? {}) as { file_name?: string; commit?: string };
    const commit = body.commit === "true";
    const total = path.includes("locations") ? 14393 : path.includes("opening") ? 2140 : 870;
    return {
      id: `imp-${Date.now()}`,
      status: commit ? "COMMITTED" : "VALIDATED",
      source_file: body.file_name,
      total_rows: total,
      valid_rows: total,
      rejected_rows: 0,
      committed_rows: commit ? total : 0,
      lp_count: commit && path.includes("opening") ? 1344 : undefined,
      movement_count: commit && path.includes("opening") ? total : undefined,
      created_at: new Date().toISOString(),
    };
  }

  // --- Tovarlar ---
  if (path === "/api/v1/skus") {
    const q = String(options.query?.q ?? "").toLowerCase();
    // `ids` — boshqa modul (LP kartasi, topshiriq satri) nomlarni
    // BITTA so'rovda boyitadi; qolgan filtrlar unga tegmaydi.
    const wanted = String(options.query?.ids ?? "").split(",").filter(Boolean);
    if (wanted.length) {
      return page(state.skus.filter((sku) => wanted.includes(sku.id)), limit);
    }
    const rows = state.skus.filter(
      (sku) =>
        (options.query?.is_active === undefined || sku.is_active === options.query.is_active) &&
        (!options.query?.missing_vgh || !sku.has_complete_vgh) &&
        (!q || sku.article.toLowerCase().includes(q) || sku.name.toLowerCase().includes(q)),
    );
    return page(rows, limit);
  }
  if (/^\/api\/v1\/skus\/[^/]+\/packages$/.test(path)) {
    return page(entities.skuPackages(path.split("/")[4]!), 100);
  }
  if (/^\/api\/v1\/skus\/[^/]+$/.test(path)) {
    const sku = state.skus.find((item) => item.id === path.split("/")[4]);
    if (method === "PATCH" && sku) {
      const body = (options.body ?? {}) as Record<string, unknown> & { version?: number };
      if (body.version !== sku.version) throw new Error("Karta boshqa foydalanuvchi tomonidan o'zgartirilgan");
      const { version: _version, ...changes } = body;
      Object.assign(sku, changes, { version: sku.version + 1, updated_at: new Date().toISOString() });
      const size = sku.dimensions as Record<string, string | undefined>;
      sku.has_complete_vgh = ["length_cm", "width_cm", "height_cm", "weight_kg"].every((key) => Number(size[key]) > 0);
    }
    return sku;
  }
  if (path === "/api/v1/control/sessions") {
    const rows = state.decisions.map((item) => item.session);
    return page(rows.filter((row) => !options.query?.status || row.status === options.query.status), limit);
  }
  if (/^\/api\/v1\/control\/sessions\/[^/]+\/discrepancies$/.test(path)) {
    const id = path.split("/")[5]!;
    return { items: state.decisions.filter((item) => item.session.id === id).map((item) => item.discrepancy) };
  }
  if (method === "POST" && /^\/api\/v1\/control\/[^/]+\/manager-decision$/.test(path)) {
    const body = (options.body ?? {}) as { decision?: string; order_change_ref?: string };
    // P07-17: qisman jo'natish faqat 1C dagi o'zgarish bilan.
    if (body.decision === "SHIP_PARTIAL" && !body.order_change_ref) {
      throw new Error("Qisman jo'natish uchun 1C buyurtma o'zgarishi kerak (P07-17)");
    }
    const id = path.split("/")[4]!;
    const item = state.decisions.find((row) => row.session.id === id);
    state.decisions = state.decisions.filter((row) => row.session.id !== id);
    return item ? { ...item.discrepancy, status: "RESOLVED", manager_decision: body.decision } : undefined;
  }
  // --- Dispetcher ---
  if (path === "/api/v1/work-queues/staff") return page(state.staff, 500);
  if (path === "/api/v1/work-queues/pending") {
    const counts = new Map<string, number>();
    for (const task of state.dispatch) counts.set(task.required_role, (counts.get(task.required_role) ?? 0) + 1);
    return { items: [...counts].map(([required_role, pending]) => ({ required_role, pending })) };
  }
  // Dispetcher navbati: tur bo'yicha, ustuvorlik tartibida —
  // serverda ham aynan shunday (`priority, created_at`).
  if (path === "/api/v1/tasks/queue") {
    const type = String(options.query?.task_type ?? "");
    const rows = state.dispatch
      .filter((task) => !type || task.task_type === type)
      .slice()
      .sort((left, right) => left.priority - right.priority ||
        left.created_at.localeCompare(right.created_at));
    return page(rows, limit);
  }
  // Topshiriqlar tarixi: filtrlar serverdagidek (holat, tur,
  // ijrochi, tugash davri, hujjat yoki ID qismi) va eng yangisi
  // birinchi. Cursor namunada — oddiy siljish.
  if (path === "/api/v1/tasks/finished") {
    const query = options.query ?? {};
    const statuses = String(query.status ?? "").split(",").filter(Boolean);
    const type = String(query.task_type ?? "");
    const assignee = String(query.assignee_id ?? "");
    const needle = String(query.q ?? "").toLowerCase();
    const from = query.from ? new Date(String(query.from)).getTime() : -Infinity;
    const to = query.to ? new Date(String(query.to)).getTime() : Infinity;
    const rows = state.finished
      .filter((task) => {
        const at = new Date(task.finished_at).getTime();
        return (
          (statuses.length === 0 || statuses.includes(task.status)) &&
          (!type || task.task_type === type) &&
          (!assignee || task.assignee_id === assignee) &&
          at >= from && at <= to &&
          (!needle ||
            (task.document_number ?? "").toLowerCase().includes(needle) ||
            task.id.includes(needle))
        );
      })
      .sort((left, right) => right.finished_at.localeCompare(left.finished_at))
      // Ro'yxat javobida satrlar YO'Q — haqiqiy API kabi.
      .map(({ lines: _lines, ...task }) => task);
    const offset = Number(query.cursor ?? 0) || 0;
    const items = rows.slice(offset, offset + limit);
    const hasMore = offset + limit < rows.length;
    return { items, has_more: hasMore, next_cursor: hasMore ? String(offset + limit) : undefined };
  }
  if (/^\/api\/v1\/tasks\/[^/]+\/history$/.test(path)) {
    const task = state.finished.find((item) => item.id === path.split("/")[4]);
    return page(task ? entities.taskStatusHistory(task) : [], limit);
  }
  if (method === "GET" && /^\/api\/v1\/tasks\/[^/]+$/.test(path)) {
    return state.finished.find((item) => item.id === path.split("/")[4]);
  }
  // Qayta rejalashtirish: namunada ham ustuvorlik HAQIQATAN
  // o'zgaradi — aks holda sudrash «ishladi» deb ko'rinib,
  // ro'yxat eski tartibda qolardi.
  if (method === "POST" && /^\/api\/v1\/tasks\/[^/]+\/replan$/.test(path)) {
    const id = path.split("/")[4]!;
    const body = (options.body ?? {}) as { priority?: number; reason?: string };
    const task = state.dispatch.find((item) => item.id === id);
    if (!task) return undefined;
    if (!body.reason?.trim()) {
      throw new Error("Qayta rejalashtirish sababi majburiy");
    }
    if (typeof body.priority === "number") task.priority = body.priority;
    task.version += 1;
    return task;
  }
  if (path === "/api/v1/tasks" && options.query?.unassigned) return page(state.dispatch, limit);
  // Hujjatdan tug'ilgan topshiriqlar: «Terishga chiqarish» va
  // «Mashina keldi» oynalari ularni xodimga biriktiradi.
  if (path === "/api/v1/tasks" && options.query?.order_id) {
    const orderId = String(options.query.order_id);
    return page(state.dispatch.filter((task) => task.order_id === orderId), limit);
  }
  if (method === "POST" && /^\/api\/v1\/tasks\/[^/]+\/assign$/.test(path)) {
    const id = path.split("/")[4]!;
    const body = (options.body ?? {}) as { assignee_id?: string };
    const task = state.dispatch.find((item) => item.id === id);
    const person = state.staff.find((item) => item.user_id === body.assignee_id);
    if (!task || !person) return undefined;
    // Server qoidasi: xodimda topshiriq talab qilgan rol bo'lishi shart.
    if (!person.roles.includes(task.required_role)) {
      throw new Error("Xodimda bu topshiriq turi uchun kerakli rol yo'q");
    }
    state.dispatch = state.dispatch.filter((item) => item.id !== id);
    person.assigned += 1;
    return { ...task, assignee_id: person.user_id };
  }

  // --- Inventarizatsiya ---
  // Rejali sanash: har 20 katak — bitta topshiriq va hujjat (server
  // bilan bir xil); namunada oxirgi raqamli katak bo'sh hisoblanadi.
  if (method === "POST" && path === "/api/v1/inventory-counts/plans") {
    const body = (options.body ?? {}) as {
      count_type?: string; freeze_mode?: string; location_codes?: string[];
      warehouse_code?: string; zone_code?: string; note?: string;
    };
    if (!body.note?.trim()) throw new Error("Inventarizatsiya sababi (izoh) ko'rsatilishi kerak");
    const type = body.count_type ?? "POINT";
    const codes = body.location_codes ?? [];
    if (type === "POINT" && codes.length === 0) throw new Error("Sanash qamrovida kamida bitta manzil bo'lishi kerak");
    const empty = codes.filter((code) => code.endsWith("9"));
    const cells = type === "POINT" ? codes.length - empty.length : 60;
    const created = Array.from({ length: Math.ceil(cells / 20) }, (_, index) => ({
      id: `cnt-plan-${state.counts.length + index}`,
      count_number: `INV-${String(4200 + state.counts.length + index).padStart(6, "0")}`,
      count_type: type,
      reason_process: "INVENTORY",
      warehouse_code: body.warehouse_code ?? "A",
      freeze_mode: body.freeze_mode ?? "BLOCK",
      status: "CREATED",
      require_separate_approver: true,
      counted_by: undefined,
      approved_by: undefined,
      task_id: `task-plan-${state.counts.length + index}`,
      created_at: new Date().toISOString(),
      version: 1,
    }));
    if (created.length === 0) throw new Error("Sanash qamrovida kamida bitta manzil bo'lishi kerak");
    state.counts = [...created, ...state.counts];
    return { counts: created, empty_location_codes: empty };
  }
  if (path === "/api/v1/inventory-counts") return page(state.counts, limit);
  if (path === "/api/v1/replenishment/policies") {
    return method === "POST" ? { id: "policy-1", ...(options.body as object) } : page([], limit);
  }
  if (/^\/api\/v1\/inventory-counts\/[^/]+\/lines$/.test(path)) {
    return page([{ id: "cl-1", location_id: "loc-1", sku_id: "sku-1", expected_qty: "19", counted_qty: "18", delta_qty: "-1" }], limit);
  }
  if (path === "/api/v1/journal") {
    // Namuna: bitta palletning yo'li — qabul, ko'tarish, joylash, terish.
    const now = Date.now();
    const at = (minutes: number) => new Date(now - minutes * 60_000).toISOString();
    return page([
      { id: "j-5", source_kind: "MOVEMENT", operation: "PICK", occurred_at: at(5), actor_id: "usr-0", lp_code: "LP00000123",
        sku_id: "sku-1", qty: "6", from_location_id: "loc-1", to_holder_id: "usr-0", task_id: "task-9", reason: "PICK_CONFIRMED" },
      { id: "j-4", source_kind: "LP", operation: "LP_LOWERED", occurred_at: at(40), actor_id: "usr-2", lp_code: "LP00000123",
        from_holder_id: "usr-2", to_location_id: "loc-1", from_status: "WITH_EMPLOYEE", to_status: "MX", reason: "PUTAWAY_PLACED" },
      { id: "j-3", source_kind: "LP", operation: "LP_LIFTED", occurred_at: at(52), actor_id: "usr-2", lp_code: "LP00000123",
        from_location_id: "loc-1", to_holder_id: "usr-2", from_status: "RECEIVING", to_status: "WITH_EMPLOYEE", reason: "PUTAWAY_LIFTED" },
      { id: "j-2", source_kind: "MOVEMENT", operation: "RECEIPT", occurred_at: at(70), actor_id: "usr-1", lp_code: "LP00000123",
        sku_id: "sku-1", qty: "48", to_location_id: "loc-1", reason: "RECEIPT_CONFIRMED" },
      { id: "j-1", source_kind: "LP", operation: "LP_CREATED", occurred_at: at(75), actor_id: "usr-1", lp_code: "LP00000123",
        to_location_id: "loc-1", to_status: "RECEIVING", reason: "ISSUED" },
    ], limit);
  }
  if (method === "POST" && path === "/api/v1/inventory-counts/apply-pending") {
    const pending = state.counts.filter((item) => item.status === "REVIEW");
    for (const count of pending) {
      count.status = "COMPLETED";
      count.version += 1;
    }
    return { applied: pending, failed: [], skipped_count: 0 };
  }
  if (method === "POST" && /^\/api\/v1\/inventory-counts\/[^/]+\/approve$/.test(path)) {
    const id = path.split("/")[4]!;
    const count = state.counts.find((item) => item.id === id);
    if (count) {
      count.status = "APPROVED";
      count.approved_by = "Dispetcher";
      count.version += 1;
    }
    return count;
  }

  // --- Xabarnomalar ---
  if (path === "/api/v1/notifications") {
    const status = options.query?.status;
    const rows = status
      ? state.notifications.filter((item) => item.status === status)
      : state.notifications;
    return page(rows, limit);
  }
  if (path === "/api/v1/notification-rules") {
    return page(entities.notificationRules(), limit);
  }
  if (method === "POST" && /^\/api\/v1\/notifications\/[^/]+\/retry$/.test(path)) {
    const id = path.split("/")[4]!;
    const item = state.notifications.find((row) => row.id === id);
    if (item) {
      item.status = "SENT";
      item.attempts += 1;
      item.sent_at = new Date().toISOString();
      item.last_error = "";
    }
    return item;
  }

  // --- Audit ---
  if (path === "/api/v1/audit-events") return page(entities.auditEvents(), limit);

  // --- Xodimlar va rollar ---
  if (path === "/api/v1/users") {
    const ids = String(options.query?.ids ?? "").split(",").filter(Boolean);
    const rows = entities.users();
    return page(ids.length > 0 ? rows.filter((row) => ids.includes(row.id)) : rows, limit);
  }
  if (path === "/api/v1/roles") return page(entities.roles(), limit);
  if (path === "/api/v1/devices") return page(entities.devices(), limit);
  // Zonalar — inventarizatsiya qamrovini tanlashda.
  if (path === "/api/v1/zones") {
    return page([
      { id: "z-1", code: "A-STORAGE", name: "A korpus saqlash va terish", zone_type: "STORAGE" },
      { id: "z-2", code: "B-STORAGE", name: "B korpus yer saqlashi", zone_type: "STORAGE" },
      { id: "z-3", code: "C-STORAGE", name: "C korpus pol saqlashi", zone_type: "STORAGE" },
      { id: "z-4", code: "QAB", name: "Qabul", zone_type: "RECEIPT" },
      { id: "z-5", code: "NAZ", name: "Nazorat", zone_type: "CONTROL" },
    ], limit);
  }
  if (path === "/api/v1/warehouses") {
    return page([{ id: "wh-a", code: "A", name: "Asosiy ombor" },
      { id: "wh-b", code: "B", name: "Sovutgich" },
      { id: "wh-c", code: "C", name: "Tranzit" }], limit);
  }

  // --- Sozlamalar ---
  // --- Serverdan tashqari zaxira ---
  //
  // Namunada bot SOZLANGAN, lekin oxirgi yuborish XATO bilan
  // tugagan: panel ikkala holatni ham ko'rsata olishi kerak.
  if (path === "/api/v1/offsite") {
    if (method === "PUT") {
      const body = (options.body ?? {}) as Record<string, string | null>;
      state.offsite = { ...state.offsite,
        chat_id: String(body.chat_id ?? state.offsite.chat_id),
        api_id: String(body.api_id ?? state.offsite.api_id),
        key_recipient_id: String(body.key_recipient_id ?? state.offsite.key_recipient_id),
        bot_token_set: state.offsite.bot_token_set || Boolean(body.bot_token),
        api_hash_set: state.offsite.api_hash_set || Boolean(body.api_hash),
        version: state.offsite.version + 1,
        updated_at: new Date().toISOString() };
    }
    return state.offsite;
  }
  if (method === "POST" && path === "/api/v1/offsite/test") {
    return { chat_title: "UYDA WMS zaxira", can_pin: true };
  }
  if (method === "POST" && path === "/api/v1/offsite/send-key") return { sent: true };

  if (path === "/api/v1/config") return page(config.resolved(), 200);
  if (path === "/api/v1/config-pending") return page(config.pending(), 200);
  if (path === "/api/v1/config-definitions") return page(config.definitions(), 200);

  const versionsMatch = /^\/api\/v1\/config\/([^/]+)\/versions$/.exec(path);
  if (versionsMatch) {
    if (method === "POST") {
      // Yangi qoralama: namuna rejimida u saqlanmaydi, lekin
      // forma muvaffaqiyatli yopilishi kerak — aks holda
      // oqimni sinab bo'lmasdi.
      return { id: "ver-new", key: versionsMatch[1]!, version: 1, status: "DRAFT" };
    }
    return page(config.versions(versionsMatch[1]!), 50);
  }
  if (method === "POST" && /^\/api\/v1\/config\/[^/]+\/versions\/[^/]+\/\w+/.test(path)) {
    return { ok: true };
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// Yordamchilar
// ---------------------------------------------------------------------------

/** Mijoz/ta'minotchi nomlari — bitta va ro'yxat so'rovi uchun. */
function counterpartyNames(): Record<string, string> {
  return { "cp-1": "Chinni Import MChJ", "cp-2": "Bahor do'koni MChJ",
    "cp-3": "Oila savdo MChJ", "cp-4": "Nur market" };
}

function page<T>(rows: T[], limit: number) {
  return { items: rows.slice(0, limit), has_more: rows.length > limit };
}

/**
 * Davr uzunligini `from`/`to` dan kunlarga aylantiradi.
 *
 * Panel sanalar yuboradi, namuna esa «1 / 7 / 30» bilan ishlaydi:
 * teng bo'lmagan oraliq eng yaqin tanlovga yaxlitlanadi.
 */
function periodDays(options: RequestOptions): number {
  const from = String(options.query?.from ?? "");
  const to = String(options.query?.to ?? "");
  if (!from || !to) return 1;

  const span = new Date(to).getTime() - new Date(from).getTime();
  const days = Math.round(span / 86_400_000) + 1;
  if (days <= 1) return 1;
  return days <= 14 ? 7 : 30;
}

function session() {
  return {
    access_token: "mock-token",
    refresh_token: "mock-refresh",
    expires_in: 3600,
    user: {
      id: "usr-mock",
      username: "namuna",
      full_name: "Namuna foydalanuvchi",
      roles: ["administrator"],
      warehouses: ["A"],
      // Vakolatlar KATALOGDAN olinadi, qo'lda yozilmaydi:
      // nomlar bo'shliq bilan boshlanadi (`identity.user.read`)
      // va qo'lda yozilganda bittasi noto'g'ri chiqib, butun
      // modul navigatsiyadan yo'qolib qolgan edi.
      permissions: Object.values(PERM),
    },
  };
}

function reportResult(name: string, options: RequestOptions) {
  const source = {
    stock: stockReport,
    "location-capacity": capacityReport,
    tasks: tasksReport,
    discrepancies: discrepancyReport,
    "incomplete-orders": incompleteOrdersReport,
    kpi: kpiReport,
    "abc-xyz": abcXyzReport,
    ...qualityReports(),
  }[name];

  if (!source) return { report: name, columns: [], items: [], has_more: false };

  const { columns, rows } = source();
  const limit = Number(options.query?.limit ?? 100);

  // Server tomonidagi filtrlar taqlid qilinadi: boshqaruv
  // paneli «faol topshiriqlar»ni SONI bo'yicha ko'rsatadi va
  // filtr e'tiborga olinmasa, raqam butun ro'yxatni
  // ko'rsatib, yolg'on bo'lardi.
  let filtered = rows;

  const status = options.query?.status;
  if (status) {
    const column = columns.indexOf("status");
    if (column !== -1) {
      filtered = filtered.filter((row) => row[column] === status);
    }
  }

  // Tovar kartasidagi «Omborda» jadvali: bitta tovarning
  // qoldig'i. Serverda bu `sku_id` filtri, hisobotda esa
  // artikul ustuni — shuning uchun ID artikulga o'giriladi.
  const skuID = options.query?.sku_id;
  if (skuID) {
    const column = columns.indexOf("sku_article");
    const sku = state.skus.find((item) => item.id === skuID);
    if (column !== -1) {
      filtered = sku ? filtered.filter((row) => row[column] === sku.article) : [];
    }
  }

  // ABC/XYZ matritsasi katagi: "NONE" — XYZ klassi berilmagan.
  for (const key of ["abc_class", "xyz_class"] as const) {
    const wanted = options.query?.[key];
    const column = columns.indexOf(key);
    if (wanted && column !== -1) {
      filtered = filtered.filter((row) =>
        wanted === "NONE" ? !row[column] : row[column] === wanted);
    }
  }

  // «Faqat muammolar»: kamomadli yoki bloklangan qatorlar.
  if (options.query?.only_issues) {
    filtered = filtered.filter((row) =>
      row.includes("ha") || row.includes("SHORTAGE") || row.includes("DISCREPANCY"));
  }

  const items = filtered.slice(0, limit).map((row) =>
    Object.fromEntries(columns.map((column, index) => [column, row[index] ?? ""])),
  );

  return { report: name, columns, items, has_more: filtered.length > limit };
}

function projectionStatus() {
  const domains = [
    "topology", "master", "inventory", "task", "control",
    "picking", "putaway", "replenishment", "inventory-count",
  ];

  return domains.map((domain, index) => ({
    projection: domain,
    last_event_type: `${domain}.changed.v1`,
    last_event_at: isoAgo(index + 1),
    applied_count: 1200 - index * 87,
    skipped_count: index,
    last_error: "",
    updated_at: isoAgo(index + 1),
  }));
}

// ---------------------------------------------------------------------------
// Xarita
// ---------------------------------------------------------------------------

/**
 * A korpusning to'qqizta qavati, B va C korpuslar.
 *
 * O'lchamlar haqiqiy reyestrga yaqin: 53 stellaj × 29 yacheyka.
 * Kichik namunada chizma bo'sh ko'rinib, uning tartibi
 * sinalmay qolardi.
 */
function mapSections() {
  const sections = [];

  // Reyestrda A korpus olti qavatli (ZON-02: 1-3 OX, 4-6 MX).
  //
  // Sanoqlar KATAKLARNING O'ZIDAN hisoblanadi: ilgari ular
  // alohida formuladan kelardi va ro'yxatdagi son chizmadagi
  // kataklar soniga to'g'ri kelmasdi.
  for (let level = 1; level <= 6; level++) {
    const cells = mapCells(`A01-${String(level).padStart(2, "0")}`);
    const occupied = cells.filter((cell) => Number(cell.on_hand_qty) > 0).length;
    const blocked = cells.filter((cell) => cell.blocked).length;

    sections.push({
      warehouse_code: "A", zone_code: "A-STORAGE",
      section: `A01-${String(level).padStart(2, "0")}`,
      aisle: "A01", level,
      kind: "RACK",
      cell_count: cells.length, occupied_cells: occupied,
      free_cells: cells.length - occupied, inactive_cells: 0,
      blocked_cells: blocked,
      pallet_capacity: cells.reduce((total, cell) => total + (cell.capacity_pallets ?? 0), 0),
      occupied_slots: occupied,
      max_row: 53, max_col: Math.max(...cells.map((cell) => cell.col ?? 0)),
    });
  }

  sections.push(
    {
      warehouse_code: "B", zone_code: "B-STORAGE", section: "B-STORAGE",
      kind: "FLOOR", cell_count: 523, occupied_cells: 118, free_cells: 405,
      inactive_cells: 0, blocked_cells: 4, pallet_capacity: 1344,
      occupied_slots: 118, max_row: 523, max_col: 0,
    },
    {
      warehouse_code: "C", zone_code: "C-STORAGE", section: "C-STORAGE",
      kind: "FLOOR", cell_count: 228, occupied_cells: 61, free_cells: 167,
      inactive_cells: 0, blocked_cells: 1, pallet_capacity: 418,
      occupied_slots: 61, max_row: 228, max_col: 0,
    },
    {
      warehouse_code: "A", zone_code: "QAB", section: "QAB",
      kind: "OPERATIONAL", cell_count: 84, occupied_cells: 12, free_cells: 72,
      inactive_cells: 0, blocked_cells: 0, pallet_capacity: 84,
      occupied_slots: 12, max_row: 84, max_col: 0,
    },
  );

  return sections;
}

/**
 * Stellajda qaysi qavatlar BOR.
 *
 * Reyestrda hamma stellaj olti qavatli emas: A01 qatorining 49
 * tasida faqat 5- va 6-qavat bor (pastida o'tish joyi), 63
 * tasida 1-qavat butun bo'yli pallet o'rni va 2-3 javon yo'q.
 * Namunada hamma stellaj to'liq bo'lsa, chizmaning qavat o'rni
 * (`ladder.ts`) sinalmay qolardi — aynan shu stellajlarda
 * 5-qavat polga tushib qolgan edi.
 */
function rackLevels(rack: number): number[] {
  if (rack % 13 === 0) return [5, 6];
  if (rack % 7 === 0) return [1, 4, 5, 6];
  return [1, 2, 3, 4, 5, 6];
}

/**
 * Qavat tuzilishi — reyestrdagidek (TZ §4.2, ZON-02).
 *
 * 1-3 qavat terish (OX): mayda Y1/Y2 yacheykalar, ko'p va past.
 * 4-6 qavat saqlash (MX): palletli PL, kam va baland.
 *
 * Balandliklar reyestrdan: 1-qavat 75 sm (PL05), 2-3 javon
 * 50 sm, 4-5 qavat 180 sm, 6-qavat 150 sm. Mayda javonsiz
 * stellajda 1-qavat butun bo'yli 190 sm li pallet o'rni —
 * u 2-3 javonlarning o'rnini egallaydi.
 *
 * Namunada hamma qavat bir xil bo'lsa, chizma haqiqiy ombordan
 * butunlay boshqacha ko'rinardi va nisbatlar sinalmay qolardi.
 */
function levelShape(level: number, rack = 1) {
  if (level === 1) {
    return rackLevels(rack).includes(2)
      ? { count: 6, type: "PL05", length: "65", width: "110", height: "75", capacity: 1 }
      : { count: 3, type: "PL", length: "80", width: "120", height: "190", capacity: 1 };
  }
  if (level === 2) return { count: 9, type: "Y1", length: "30", width: "100", height: "50", capacity: undefined };
  if (level === 3) return { count: 4, type: "Y2", length: "55", width: "100", height: "50", capacity: undefined };
  return {
    count: 3, type: "PL", length: "80", width: "120",
    height: level === 6 ? "150" : "180", capacity: 1,
  };
}

/**
 * Issiq nuqtalar: katakka oynada necha marta tegilgani.
 *
 * Harakat TASODIFIY emas, qoidali: terish qavatlari (1-3, OX)
 * saqlash qavatlaridan (4-6, MX) ko'p marta tegiladi va
 * qatorning boshidagi stellajlar oxiridagilaridan issiqroq —
 * omborda ham shunday, chiqish eshigiga yaqin joy ko'proq
 * ishlaydi. Tasodifiy sonlarda chizma shunchaki shovqin bo'lib
 * ko'rinardi va shkala sinalmay qolardi.
 *
 * Oyna raqamga TA'SIR QILADI: 7 kunlik ko'rinish 90 kunlikdan
 * sezilarli past bo'lishi kerak, aks holda oynani almashtirish
 * hech nima o'zgartirmagandek tuyulardi.
 */
/**
 * Katak tarkibi: unda qanday tovar turibdi.
 *
 * Namunada og'irlik va hajm BOR, chunki modalning butun gapi shu.
 * Lekin har uchinchi tovarda VGH yo'q — proddagi holat aynan
 * shunday (57 126 tovardan 0 tasida o'lcham bor) va interfeys
 * «VGH yo'q» holatini ko'rsatishi sinalishi kerak.
 */
function mapCellContents(locationID: string) {
  const random = seeded(locationID.length * 29 + 7);
  const parts = locationID.split("-");
  const level = Number(parts[1] ?? 1);
  const count = level <= 3 ? 1 + Math.floor(random() * 2) : 1 + Math.floor(random() * 3);

  const items = [];
  let totalQty = 0;
  let totalWeight = 0;
  let totalVolume = 0;
  let measured = 0;

  for (let index = 0; index < count; index++) {
    const sku = state.skus[(locationID.length * 3 + index * 7) % state.skus.length]!;
    const quantity = Math.round(random() * 120) + 4;
    const hasVGH = Boolean(sku.has_complete_vgh);
    const weight = hasVGH ? quantity * Number(sku.dimensions.weight_kg ?? 0) : 0;
    const volume = hasVGH
      ? quantity *
        Number(sku.dimensions.length_cm ?? 0) *
        Number(sku.dimensions.width_cm ?? 0) *
        Number(sku.dimensions.height_cm ?? 0)
      : 0;

    items.push({
      sku_id: sku.id,
      article: sku.article,
      name: sku.name,
      on_hand_qty: String(quantity),
      reserved_qty: String(Math.round(quantity * random() * 0.3)),
      lp_count: level > 3 ? 1 + Math.round(random()) : 0,
      lot_count: index === 0 && random() > 0.7 ? 1 : 0,
      weight_kg: weight.toFixed(3),
      volume_cm3: String(Math.round(volume)),
      has_vgh: hasVGH,
    });

    totalQty += quantity;
    if (hasVGH) {
      measured += 1;
      totalWeight += weight;
      totalVolume += volume;
    }
  }

  return {
    items,
    total_skus: items.length,
    measured_skus: measured,
    total_qty: String(totalQty),
    total_weight_kg: totalWeight.toFixed(3),
    total_volume_cm3: String(Math.round(totalVolume)),
  };
}

/**
 * Stellajning ABC hududi.
 *
 * Chiqishga yaqin stellajlar — A: eng tez aylanadigan tovar eng
 * qisqa marshrutni olishi kerak (PAR-03).
 */
function abcZone(rack: number): string {
  if (rack <= 18) return "A";
  if (rack <= 36) return "B";
  return "C";
}

function mapHeat(section: string, aisle: string, days: number) {
  const wanted = new Set(
    (aisle
      ? Array.from({ length: 6 }, (_, index) =>
          mapCells(`${aisle}-${String(index + 1).padStart(2, "0")}`),
        ).flat()
      : mapCells(section)
    ).map((cell) => cell.location_id),
  );

  // Shkala BUTUN KORPUS bo'yicha — ko'rinayotgan kataklardan
  // emas. Server ham shunday ishlaydi va namunada boshqacha
  // bo'lsa, legendadagi «qatorlarni solishtirsa bo'ladi» degan
  // yozuv yolg'on bo'lardi.
  const all = [
    ...Array.from({ length: 6 }, (_, index) =>
      mapCells(`A01-${String(index + 1).padStart(2, "0")}`),
    ).flat(),
    ...mapCells("QAB"),
  ];

  const random = seeded(days * 13 + 11);
  const scaleDays = Math.max(days, 1) / 30;

  const items = [];
  const scaleMoves = [];
  for (const cell of all) {
    const picking = (cell.level ?? 4) <= 3;
    // Qator boshi — chiqishga yaqin: 1.6 dan 0.4 gacha pasayadi.
    const nearExit = 1.6 - ((cell.row ?? 1) / 53) * 1.2;
    const base = (picking ? 9 : 2) * nearExit * (0.3 + random());
    const moves = Math.round(base * scaleDays);
    if (moves <= 0) continue;
    scaleMoves.push(moves);
    if (!wanted.has(cell.location_id)) continue;
    items.push({
      location_id: cell.location_id,
      moves,
      // Terish — chiqish harakatlarining taxminan yarmi.
      picks: picking ? Math.round(moves * 0.6) : Math.round(moves * 0.2),
    });
  }

  const sorted = scaleMoves.sort((a, b) => a - b);
  const at = (share: number) =>
    sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * share))] ?? 0;

  return {
    window_days: days,
    scale: {
      cells: sorted.length,
      max: sorted[sorted.length - 1] ?? 0,
      median: at(0.5),
      p90: at(0.9),
    },
    items,
  };
}

function mapCells(section: string) {
  const random = seeded(section.length * 17 + 3);
  const rack = /^A01-(\d+)$/.exec(section);

  if (rack) {
    const level = Number(rack[1]);
    const cells = [];
    for (let row = 1; row <= 53; row++) {
      // Bu stellajda shunday qavat umuman yo'q.
      if (!rackLevels(row).includes(level)) continue;
      const shape = levelShape(level, row);
      for (let col = 1; col <= shape.count; col++) {
        // Reyestrda yo'q o'rin — chizmada TESHIK bo'lib
        // ko'rinadi va bu ataylab.
        if (row > 50 && col === shape.count) continue;

        const roll = random();
        const occupied = roll > 0.93;
        const blocked = roll < 0.05 && row < 30;

        cells.push({
          location_id: `loc-${level}-${row}-${col}`,
          code: `A01-${String(row).padStart(2, "0")}-${String(level).padStart(2, "0")}-${String(col).padStart(2, "0")}`,
          zone_code: "A-STORAGE",
          location_function: level <= 3 ? "OX" : "MX",
          cell_type: shape.type,
          // ABC hududi QATOR BO'YLAB: chiqishga yaqin stellajlar
          // A, uzoqlari C — reyestrda ham shunday.
          abc_zone: abcZone(row),
          // Katakda turgan tovarning toifasi. Har oltinchisi
          // O'Z HUDUDIDA EMAS: chizmadagi «noto'g'ri joy»
          // belgisi sinalmay qolmasligi kerak.
          sku_abc_class: occupied
            ? row % 6 === 0
              ? abcZone(row === 53 ? 1 : row + 17)
              : abcZone(row)
            : undefined,
          row, col, level,
          capacity_pallets: shape.capacity,
          occupied_slots: occupied ? 1 : 0,
          free_slots: shape.capacity ? (occupied ? 0 : 1) : 0,
          on_hand_qty: occupied ? String(Math.round(roll * 400)) : "0",
          distinct_sku_count: occupied ? Math.round(roll * 8) + 1 : 0,
          // Reyestrdagi o'lchamlar: MX palletli katak va OX mayda
          // terish katagi (TZ §4.2). Har beshinchisida o'lcham
          // YO'Q — interfeys katak turidan nominal qiymat olishi
          // sinalsin.
          // Har o'ninchisida o'lcham YO'Q: interfeys katak
          // turidan nominal qiymat olishi sinalsin.
          ...(row % 10 === 0
            ? { length_cm: "0", width_cm: "0", height_cm: "0", max_weight_kg: "0" }
            : {
                length_cm: shape.length,
                width_cm: shape.width,
                height_cm: shape.height,
                max_weight_kg: shape.capacity ? "1000" : "150",
              }),
          is_active: true,
          putaway_allowed: !blocked,
          blocked,
        });
      }
    }
    return cells;
  }

  const total = section === "B-STORAGE" ? 523 : section === "C-STORAGE" ? 228 : 84;
  const prefix = section === "QAB" ? "QAB-" : section.charAt(0);

  return Array.from({ length: total }, (_, index) => {
    const roll = random();
    const occupied = roll > 0.77;

    return {
      location_id: `loc-${section}-${index}`,
      code: section === "QAB"
        ? `${prefix}${String(index + 1).padStart(3, "0")}`
        : `${prefix}${String(index + 1).padStart(3, "0")}`,
      zone_code: section,
      location_function: section === "QAB" ? "RECEIPT" : "MX",
      cell_type: section === "QAB" ? "OP" : "P2",
      // Yerda saqlash va operatsion zonalarda ABC hududi YO'Q:
      // u faqat stellajli korpusda ma'noga ega.
      abc_zone: undefined,
      sku_abc_class: undefined,
      row: index + 1,
      col: undefined,
      level: undefined,
      capacity_pallets: section === "B-STORAGE" ? 3 : 2,
      occupied_slots: occupied ? 1 : 0,
      free_slots: occupied ? 1 : 2,
      on_hand_qty: occupied ? String(Math.round(roll * 900)) : "0",
      distinct_sku_count: occupied ? Math.round(roll * 4) + 1 : 0,
      length_cm: "160", width_cm: "120", height_cm: "250", max_weight_kg: "2000",
      is_active: true,
      putaway_allowed: roll > 0.03,
      blocked: roll < 0.03,
    };
  });
}
